<!-- @component
  Manages and displays the materials assigned to a QuantityUnit, grouped by
  catalog with collapse/expand, filtering, and add/remove.
  Materials are stored as node IDs; catalog and material names are resolved
  from mmpvState.catalogs at render time.
-->
<script lang="ts">
	import type { Catalog } from './types/catalog'
	import type { Material } from './types/material'
	import { mmpvState } from '#lib/mmpv/mmpv.state.svelte.js'
	import Button from '#lib/atoms/Button.svelte'
	import { t } from '$i18n/i18n'
	import { untrack } from 'svelte'
	import { SvelteSet } from 'svelte/reactivity'
	import AutocompleteInputField from '#lib/autocomplete/AutocompleteInputField.svelte'
	import { DefaultInputFieldProps } from '$types/fontProps'
	import IconChevronDown from '$svg/icon-chevron-down.svg?component'
	import Badge from '#lib/atoms/Badge.svelte'
	import { fly } from 'svelte/transition'
	import IconCross from '$svg/icon-cross.svg?component'

	interface Props {
		quantityPublicId: string
		materialNodeIds?: string[]
	}

	let { quantityPublicId, materialNodeIds = $bindable([]) }: Props = $props()

	let internalNodeIds: string[] = $state([...(materialNodeIds ?? [])])

	$effect(() => {
		const incoming = materialNodeIds ?? []
		const current = untrack(() => internalNodeIds)
		if (incoming.join(',') !== current.join(',')) {
			internalNodeIds = [...incoming]
		}
	})

	const fontProps = DefaultInputFieldProps()

	let selectedCatalogId: string | null = $state(null)
	let selectedNodeId: string | null = $state(null)
	let materialFilter: string = $state('')
	let duplicateError: boolean = $state(false)
	const collapsedCatalogs: SvelteSet<string> = new SvelteSet()

	const otherQuantityNodeIds = $derived.by(() => {
		const result = new SvelteSet<string>()
		for (const q of mmpvState.quantities) {
			if (q.publicId !== quantityPublicId) {
				for (const id of q.materialNodeIds ?? []) {
					result.add(id)
				}
			}
		}
		return result
	})

	const catalogOptions = $derived(
		mmpvState.catalogs.map((c: Catalog) => ({ value: c.id, label: c.name }))
	)

	function buildNodeLabel(node: Material, catalog: Catalog): string {
		const code = (node.parentCode ?? '') + node.code
		const indicator = node.highlighted && catalog.highlightedValue ? catalog.highlightedValue : ''
		return `${code}${indicator} ${node.name}`.trim()
	}

	const availableNodeOptions = $derived.by(() => {
		if (!selectedCatalogId) return []
		const catalog = mmpvState.catalogs.find((c: Catalog) => c.id === selectedCatalogId)
		if (!catalog) return []
		const localIds = new Set(internalNodeIds)
		return (catalog.nodes ?? [])
			.filter((node: Material) => !otherQuantityNodeIds.has(node.id) && !localIds.has(node.id))
			.map((node: Material) => ({ value: node.id, label: buildNodeLabel(node, catalog) }))
	})

	function addMaterial() {
		if (!selectedCatalogId || !selectedNodeId) return
		if (internalNodeIds.includes(selectedNodeId)) {
			duplicateError = true
			return
		}
		duplicateError = false
		internalNodeIds = [...internalNodeIds, selectedNodeId]
		materialNodeIds = [...internalNodeIds]
		selectedNodeId = null
	}

	function removeMaterial(nodeId: string) {
		internalNodeIds = internalNodeIds.filter((id) => id !== nodeId)
		materialNodeIds = [...internalNodeIds]
	}

	function toggleCatalog(catalogName: string) {
		if (collapsedCatalogs.has(catalogName)) {
			collapsedCatalogs.delete(catalogName)
		} else {
			collapsedCatalogs.add(catalogName)
		}
	}

	function resolveNode(nodeId: string): { catalog: Catalog; node: Material } | null {
		for (const catalog of mmpvState.catalogs) {
			const node = (catalog.nodes ?? []).find((n: Material) => n.id === nodeId)
			if (node) return { catalog, node }
		}
		return null
	}

	type GroupedMaterial = { nodeId: string; name: string; catalogName: string }
	type CatalogGroup = { catalogName: string; items: GroupedMaterial[] }

	const groupedMaterials = $derived.by((): CatalogGroup[] => {
		const resolved: GroupedMaterial[] = internalNodeIds
			.map((id) => {
				const info = resolveNode(id)
				return info
					? {
							nodeId: id,
							name: buildNodeLabel(info.node, info.catalog),
							catalogName: info.catalog.name
						}
					: null
			})
			.filter((m): m is GroupedMaterial => m !== null)

		const filtered = materialFilter
			? resolved.filter(
					(m) =>
						m.name.toLowerCase().includes(materialFilter.toLowerCase()) ||
						m.catalogName.toLowerCase().includes(materialFilter.toLowerCase())
				)
			: resolved

		return filtered.reduce<CatalogGroup[]>((groups, item) => {
			const group = groups.find((g) => g.catalogName === item.catalogName)
			if (group) {
				group.items.push(item)
			} else {
				groups.push({ catalogName: item.catalogName, items: [item] })
			}
			return groups
		}, [])
	})
</script>

<div class="materials-section">
	<hr class="section-divider" />

	<span class="materials-title">{$t('UI.mmpv.quantities.materials.add.title')}</span>

	<div class="add-material-row">
		<div class="autocomplete-fields">
			<AutocompleteInputField
				bind:value={selectedCatalogId}
				options={catalogOptions}
				{fontProps}
				label={$t('UI.mmpv.quantities.materials.catalog')}
				placeholder={$t('UI.mmpv.quantities.materials.catalog.ph')}
				onChange={() => {
					selectedNodeId = null
					duplicateError = false
				}}
			/>
			<AutocompleteInputField
				bind:value={selectedNodeId}
				options={availableNodeOptions}
				{fontProps}
				label={$t('UI.mmpv.quantities.materials.material')}
				placeholder={$t('UI.mmpv.quantities.materials.material.ph')}
				disabled={!selectedCatalogId}
				onChange={() => {
					duplicateError = false
				}}
			/>
		</div>
		<Button
			variant="primary"
			color="green"
			onclick={addMaterial}
			disabled={!selectedCatalogId || !selectedNodeId}
		>
			{$t('UI.mmpv.quantities.materials.add')}
		</Button>
	</div>
	{#if duplicateError}
		<span class="duplicate-error">{$t('UI.mmpv.quantities.materials.duplicateError')}</span>
	{/if}

	<span class="materials-title">{$t('UI.mmpv.quantities.materials.title')}</span>

	{#if internalNodeIds.length > 0}
		<div class="filter-input-wrapper">
			<input
				class="filter-input"
				type="text"
				bind:value={materialFilter}
				placeholder={$t('UI.mmpv.quantities.materials.filterPh')}
			/>
			{#if materialFilter}
				<button
					type="button"
					class="filter-clear-btn"
					aria-label={$t('UI.mmpv.quantities.materials.filterClear')}
					onclick={() => (materialFilter = '')}
				>
					<IconCross width={14} height={14} fill="var(--color-gray-500)" />
				</button>
			{/if}
		</div>
	{/if}

	<div class="materials-list">
		{#if groupedMaterials.length === 0}
			<span class="empty-state">{$t('UI.mmpv.quantities.materials.empty')}</span>
		{:else}
			{#each groupedMaterials as group (group.catalogName)}
				<div class="catalog-group">
					<button
						type="button"
						class="catalog-header"
						onclick={() => toggleCatalog(group.catalogName)}
					>
						<Badge
							text={group.catalogName}
							backgroundColor="var(--color-green-200)"
							color="var(--color-blueish-800)"
						/>
						<span class="catalog-count">
							{group.items.length === 1
								? $t('UI.mmpv.quantities.materials.countSingle')
								: $t('UI.mmpv.quantities.materials.countPlural', { count: group.items.length })}
						</span>
						<span class="collapse-icon" class:collapsed={!collapsedCatalogs.has(group.catalogName)}>
							<IconChevronDown width={20} height={20} />
						</span>
					</button>
					{#if !collapsedCatalogs.has(group.catalogName)}
						{const flyAnimation = { duration: 300, y: -15, delay: 25 }}
						<div class="materials-wrapper" in:fly={flyAnimation} out:fly={flyAnimation}>
							{#each group.items as item (item.nodeId)}
								<div class="material-row">
									<span class="material-name">{item.name}</span>
									<button
										type="button"
										class="remove-btn"
										aria-label={$t('UI.mmpv.quantities.materials.remove')}
										onclick={() => removeMaterial(item.nodeId)}
									>
										<IconCross width={18} height={18} fill="var(--color-gray-600)" />
									</button>
								</div>
							{/each}
						</div>
					{/if}
				</div>
			{/each}
		{/if}
	</div>
</div>

<style lang="scss">
	.materials-section {
		width: 100%;
		display: flex;
		flex-direction: column;
		gap: 8px;
		margin-top: 4px;
	}

	.section-divider {
		width: 100%;
		border: none;
		border-top: 1px solid var(--color-gray-200);
		margin: 8px 0 4px;
	}

	.materials-title {
		margin-bottom: 8px;

		font-size: 13.5px;
		font-weight: 600;
		color: #000;
	}

	.add-material-row {
		display: flex;
		flex-direction: row;
		gap: 8px;
		align-items: center;
		margin-bottom: 16px;
	}

	.autocomplete-fields {
		flex: 1;
		display: flex;
		gap: 8px;
		min-width: 0;

		:global(> .input-field-error-message-container) {
			flex: 1;
			min-width: 0;
		}
	}

	.filter-input-wrapper {
		position: relative;
		width: 100%;
		margin-bottom: 4px;
	}

	.filter-input {
		width: 100%;
		height: 32px;
		border: 1px solid var(--color-gray-200);
		border-radius: 4px;
		padding: 0 32px 0 8px;
		font-size: 13px;
		color: var(--color-gray-900);

		&:focus-visible,
		&:focus-within {
			outline: 2px solid var(--primary-green);
		}
	}

	.filter-clear-btn {
		position: absolute;
		right: 6px;
		top: 50%;
		transform: translateY(-50%);
		display: flex;
		align-items: center;
		justify-content: center;
		background: none;
		border: none;
		cursor: pointer;
		padding: 2px;
		color: var(--color-gray-500);

		&:hover :global(svg path) {
			fill: var(--color-gray-700);
		}
	}

	.materials-list {
		width: 100%;
		display: flex;
		flex-direction: column;
		gap: 8px;
	}

	.empty-state {
		font-size: 13px;
		color: var(--color-gray-400);
		padding: 8px 0;
	}

	.catalog-group {
		display: flex;
		flex-direction: column;
	}

	.catalog-header {
		display: flex;
		align-items: center;
		gap: 8px;
		background: var(--color-gray-50);
		border: 1px solid var(--color-gray-200);
		border-radius: 4px;
		padding: 4px 8px 4px;
		cursor: pointer;
		width: 100%;
		text-align: left;
	}

	.catalog-count {
		font-size: 12px;
		color: var(--color-gray-600);
	}

	.collapse-icon {
		color: var(--color-gray-400);
		margin-left: auto;
		display: flex;
		align-items: center;
		transition: transform 0.15s ease;

		&.collapsed {
			transform: rotate(-180deg);
		}
	}

	.materials-wrapper {
		max-height: 200px;
		overflow-y: auto;
	}

	.material-row {
		display: flex;
		align-items: center;
		justify-content: space-between;
		column-gap: 4px;
		padding: 6px 8px 6px 16px;
		font-size: 13px;
		border-radius: 4px;

		&:not(:first-of-type) {
			border-top: 1px solid var(--color-gray-200);
		}

		&:hover {
			background: var(--color-blueish-50);
		}
	}

	.material-name {
		flex: 1;
		min-width: 0;
		overflow: hidden;
		text-overflow: ellipsis;
		white-space: nowrap;
	}

	.remove-btn {
		background: none;
		border: none;
		cursor: pointer;
		color: var(--color-gray-400);
		font-size: 16px;
		line-height: 1;
		padding: 0 2px;

		&:hover {
			color: var(--color-red-600);
		}
	}

	.duplicate-error {
		font-size: 12px;
		color: var(--color-red-600);
	}
</style>
